import { Injectable } from '@angular/core'
import { CountryService } from '../../../site-context/facade/country.service'
import { map, take, tap } from 'rxjs/operators'
import { MbConstants } from '../../../shared/mb-constants'
import { WindowRef } from '@spartacus/core'
import { BehaviorSubject, from, Observable, of } from 'rxjs'
import { AutocompleteOptions, Facet, FacetCount, Page, SmartSearch, SmartSearchOptions, URLParam } from 'smartsearch.js'
import { environment as env } from '../../../../../environments/environment'
import {
  CertificatesResultItem, ClinicalChangesResultItem, CmsFacet, CmsResultData, JobResultItem, SearchUrlParams, VideoResultItem
} from '../types'
import { ResultItemFlexType } from '../../enums/result-item-flex-type'
import { ListType } from '../../enums/list-type'
import { AdditionalInfo } from '../../enums/additional-info'
import { ObjectUtils } from '../../../shared/utils/object-utils'
import { ContentType } from '../../enums/content-type'
import { StringUtil } from '../../../shared/utils/string-util'

@Injectable({
  providedIn: 'root'
})
export class CmsSearchService {

  activeCountry$ = new BehaviorSubject(MbConstants.FALLBACK_COUNTRY.toLowerCase())

  constructor (private countryService: CountryService,
               private winRef: WindowRef) {
    if (this.winRef.isBrowser()) {
      this.countryService.getActive().pipe(
        take(1),
        map(country => country?.toLowerCase() || MbConstants.FALLBACK_COUNTRY.toLowerCase()),
        tap(country => this.activeCountry$.next(country))
      ).subscribe()
    }
  }

  preparedSearch (preparedSearch: string, customParams: URLParam[] = [],
                  autocompleteOptions?: AutocompleteOptions): SmartSearch | undefined {
    preparedSearch = preparedSearch || env.smartSearchDefaultCaasGenerator
    customParams.push(...[{ language: 'xx' }, { language: this.activeCountry$.value }])
    const options: SmartSearchOptions = {
      customParams,
      autocompleteOptions
    }
    // smartSearch currently does not support SSR
    return this.winRef.isBrowser() && new SmartSearch(env.smartSearchUrl, preparedSearch, options) || undefined
  }

  autocomplete (query: string, params: URLParam[], field: string, preparedSearch: string): Observable<string[]> {
    if (!query || !preparedSearch || !field) {
      return of([])
    }
    params = [{ field: field }, ...params]
    const autocompleteOptions = {
      prefixThreshold: 3,
    }
    return from(this.preparedSearch(preparedSearch, params, autocompleteOptions)?.fetchAutocompleteList(query) ?? of([])).pipe(
      map(result => result ?? []))
  }

  toggleBodyClass (className: string, add: boolean): void {
    return add
      ? this.winRef.document.body.classList.add(className)
      : this.winRef.document.body.classList.remove(className)
  }

  hasBodyClass (className: string): boolean {
    return this.winRef.document.body.classList.contains(className)
  }

  searchCertificates (query: string = '*', params: URLParam[] = []): Observable<Page | undefined> {
    return from(this.preparedSearch(env.smartSearchCertificates, params)?.search(query) ?? Promise.resolve(undefined))
  }

  searchDocuments (query: string = '*', params: URLParam[] = []): Observable<Page | undefined> {
    return from(this.preparedSearch(env.smartSearchDocuments, params)?.search(query) ?? Promise.resolve(undefined))
  }

  searchContent (query: string = '*', params: URLParam[] = []): Observable<Page | undefined> {
    return from(this.preparedSearch(env.smartSearchContent, params)?.search(query) ?? Promise.resolve(undefined))
  }

  searchClinicalChanges (query: string = '*', params: URLParam[] = []): Observable<Page | undefined> {
    return from(this.preparedSearch(env.smartSearchClinicalChanges, params)?.search(query || '*') ?? Promise.resolve(undefined))
  }

  searchJobs (query: string = '*', params: URLParam[] = []): Observable<Page | undefined> {
    return from(this.preparedSearch(env.smartSearchJobs, params)?.search(query || '*') ?? Promise.resolve(undefined))
  }

  searchByField (query: string = '*', params: URLParam[] = [], field: string, preparedSearch: string): Observable<Page | undefined> {
    const fieldParam = { fq: `${field}:"${query}"` }
    params = [fieldParam, ...params]
    return from(this.preparedSearch(preparedSearch, params)?.search('*') ?? Promise.resolve(undefined))
  }

  mapResultItemsByListType (resultItems: any[] = [], listType: ListType, additionalInfo: AdditionalInfo): any[] {
    return resultItems.map(resultItem => this.mapResultItemByListType(resultItem, listType, additionalInfo))
  }

  mapResultItemByListType (resultItem: any, listType: ListType, additionalInfo: AdditionalInfo): any {
    switch (listType) {
      case ListType.CCN:
        return this.mapClinicalChangesResultItem(resultItem?.result)
      case ListType.CERT:
        return this.mapCertificatesResultItem(resultItem?.result)
      case ListType.DOCUMENT:
        return this.mapDocumentResultItem(resultItem?.result)
      case ListType.CONTENT:
        return this.mapContentResultItem(resultItem?.result, additionalInfo)
      case ListType.JOB:
        return this.mapJobResultItem(resultItem?.result)
      default:
        return {}
    }
  }

  mapReadonlyFacetsToFacets (cmsFacets: CmsFacet[]): Facet[] {
    let facets: Facet[] = []
    cmsFacets
      .filter(facet => facet.category !== MbConstants.CATEGORY_FACET_CODE)
      .map(facet => this.createFacetFromCmsFacet(facet))
      .forEach(facet => (facets = this.mergeFacet(facet, facets)))
    return facets
  }

  mergeFacet (facetToMerge: Facet, facets: Facet[]): Facet[] {
    if (!facets.find(facet => facet.config.baseName === facetToMerge.config.baseName)) {
      return facets.concat(facetToMerge)
    }
    return facets.map(facet => {
      if (facet.config.baseName === facetToMerge.config.baseName) {
        return {
          ...facet,
          counts: facet.counts.concat(facetToMerge.counts.filter(f => !facet.counts.find(fc => fc.value === f.value)))
        } as Facet
      }
      return facet
    })
  }

  createFacetFromCmsFacet (cmsFacet: CmsFacet): Facet {
    return {
      config: {
        baseName: (cmsFacet?.category ?? '').replaceAll(' ', '_')
      },
      counts: [this.mapCMSFacetToFacetCount(cmsFacet, true)]
    } as Facet
  }

  mapCMSFacetToFacetCount (facet: CmsFacet, isReadOnly = false): FacetCount {
    const categoryName = facet.category.replaceAll(' ', '_')
    return {
      value: facet.name,
      count: 0,
      filterQuery: `${categoryName}_facet_string: ${facet.name}`,
      urlFilterQuery: `facet.filter.${categoryName}=${StringUtil.replaceSpaceWithPlus(facet.name)}`,
      isReadOnly
    }
  }

  private mapCertificatesResultItem (resultItem: CmsResultData): CertificatesResultItem {
    return {
      ...resultItem,
      link: resultItem?.link ?? '',
      title: resultItem?.title?.[0] ?? '',
      contentType: ContentType.CERTIFICATES,
      flexType: ResultItemFlexType.RESULT_ITEM_CERTIFICATES
    } as CertificatesResultItem
  }

  private mapClinicalChangesResultItem (resultItem: CmsResultData): ClinicalChangesResultItem {
    return {
      ...resultItem,
      link: resultItem?.link ?? '',
      title: resultItem?.title?.[0] ?? '',
      contentType: ContentType.CCN,
      flexType: ResultItemFlexType.RESULT_ITEM_CLINICAL_CHANGES
    } as ClinicalChangesResultItem
  }

  private mapDocumentResultItem (resultItem: CmsResultData): ClinicalChangesResultItem {
    return {
      ...resultItem,
      link: resultItem?.link ?? '',
      title: resultItem?.title?.[0] ?? '',
      contentType: resultItem.Content_type_keyword?.[0] ?? 'PDF',
      flexType: ResultItemFlexType.RESULT_ITEM_DOCUMENT
    } as ClinicalChangesResultItem
  }

  private mapContentResultItem (resultItem: CmsResultData, additionalInfo: AdditionalInfo): any {
    if (resultItem?.stills) {
      return this.mapVideoResultItem(resultItem)
    }
    if (resultItem?.jobField?.length) {
      return this.mapJobResultItem(resultItem)
    }
    const formData = resultItem?.formData as any
    const keywords = (formData?.pt_tags?.value ?? [])
      .map((keyword: any) => keyword?.value?.target?.name ?? '')
      .filter((keyword: string) => keyword.length)
    const description = resultItem?.FS_pt_meta_description?.[0] ?? ''
    const contentType = keywords.find((tag: any) => tag?.value?.target?.category === MbConstants.CONTENT_TYPE)?.value?.target?.name
      || resultItem?.Content_type_keyword?.[0]?.trim()
      || ''
    const hasText = (additionalInfo === AdditionalInfo.DESCRIPTION && description.length)
      || (additionalInfo === AdditionalInfo.KEYWORD && keywords.length)
    const faqSnippet = resultItem?.faqSnippetExtracted?.[0]
    const scheme = resultItem?.scheme?.[0] ?? ''
    return {
      flexType: faqSnippet?.length ? ResultItemFlexType.RESULT_ITEM_FAQ : ResultItemFlexType.RESULT_ITEM_CONTENT,
      link: resultItem?.link ?? '',
      seoURL: resultItem?.FS_pt_seoUrl?.pop() || resultItem?.link || '',
      description,
      keywords,
      title: (resultItem?.FS_pt_pageTitle as any)?.[0] || resultItem?.title?.[0] || '',
      contentType,
      hasText,
      isPDF: scheme === 'document',
      faqSnippet: faqSnippet,
      additionalInfo: additionalInfo,
      scheme
    }
  }

  private mapJobResultItem (data: CmsResultData | undefined): JobResultItem {
    return {
      address: data?.address?.shift() ?? '',
      city: data?.cityName?.shift() ?? '',
      company: data?.company?.shift() ?? '',
      contactName: data?.contactName?.shift() ?? '',
      contactPhone: data?.contactPhone?.shift() ?? '',
      content: data?.content?.shift() ?? '',
      contentType: ContentType.JOB,
      countryIso: data?.countryIsoCode?.shift() ?? '',
      countryName: data?.countryName?.shift() ?? '',
      division: data?.division?.shift() ?? '',
      employmentType: data?.employmentType?.shift() ?? '',
      experience: data?.experienceLevel?.shift() ?? '',
      externalLink: data?.externalLink?.shift() ?? '',
      flexType: ResultItemFlexType.RESULT_ITEM_JOB,
      id: data?.id ?? '',
      jobId: data?.jobId?.shift() ?? '',
      jobField: data?.jobField?.shift() ?? '',
      link: data?.link ?? '',
      provider: data?.provider?.shift() ?? '',
      startDate: data?.startDate?.shift() ?? '',
      title: data?.title?.shift() ?? '',
      videoId: data?.videoId?.shift() ?? '',
      workTime: data?.workTime?.shift() ?? ''
    }
  }

  private mapVideoResultItem (resultItem: CmsResultData): VideoResultItem {
    const description = resultItem?.description?.[0] ?? ''
    return {
      flexType: ResultItemFlexType.RESULT_ITEM_VIDEO,
      link: resultItem?.link ?? '',
      contentType: ContentType.VIDEO,
      id: resultItem?.uid?.[0] ?? '',
      source: resultItem,
      description: description === 'null' ? '' : description,
      title: resultItem?.title?.[0] ?? '',
      length: +(resultItem?.length?.[0] ?? 0),
      channels: (resultItem?.channels as any ?? []).map((channel: string) => ObjectUtils.parseJSON(channel)),
      stills: (resultItem?.stills ?? []).map((still: string) => ObjectUtils.parseJSON(still)),
      deleted: resultItem?.deleted?.[0] === 'true' ?? false,
      published: resultItem?.published?.[0] === 'true' ?? false,
      thumbnail: resultItem?.thumbnail ?? '',
      downloadable: resultItem?.downloadable?.[0] === 'true' ?? false
    }
  }

  mapFilterParamToFacetCounts (params: SearchUrlParams, listType: ListType): Facet[] {
    let filter = listType === ListType.CONTENT ? params?.filterContent ?? '' : params?.filterJob ?? ''
    if (listType === ListType.CONTENT) {
      filter = params?.filterContent ?? ''
    }
    if (listType === ListType.DOCUMENT) {
      filter = params?.filterDocument ?? ''
    }
    if (listType === ListType.JOB) {
      filter = params?.filterJob ?? ''
    }
    if (!filter.length) {
      return []
    }
    const facetFilters = filter
      .split('&')
      .filter(f => f && f.length && f.startsWith('facet.filter.'))
      .filter(f => !f?.startsWith(`facet.filter.${MbConstants.CATEGORY_FACET_CODE}=`))

    return facetFilters
      .map(f => f?.split('=') ?? [])
      .map(([facetKey, facetValue]) => {
        const facetName = facetKey.replace('facet.filter.', '').replaceAll(' ', '_')
        return {
          config: {
            baseName: facetName
          },
          counts: [{
            count: 0,
            value: StringUtil.replacePlusWithSpace(decodeURIComponent(facetValue)),
            filterQuery: `facet.filter.${facetName}:${facetValue}`,
            urlFilterQuery: `facet.filter.${facetName}=${facetValue}`
          }] as FacetCount[]
        } as Facet
      })
  }
}
